import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldTitle, useInput, useTranslate } from 'ra-core';
import { InputHelperText } from 'ra-ui-materialui';
import TextField from '@material-ui/core/TextField';

const sanitizeRestProps = ({
  allowEmpty,
  alwaysOn,
  basePath,
  component,
  defaultValue,
  format,
  formClassName,
  initialValue,
  initializeForm,
  input,
  isRequired,
  label,
  limitChoicesToValue,
  locale,
  meta,
  options,
  optionText,
  optionValue,
  parse,
  record,
  resource,
  source,
  textAlign,
  translate,
  translateChoice,
  ...rest
}) => rest;

/**
 * WordleLettersInput
 * - Captures exactly 5 logical "letters" (tokens), where a single token can be multi-character (e.g. SZ, TY, DZS)
 * - Stores value as an array of 5 uppercase strings in the field specified by `source`
 */
const MAX_TOKEN_LENGTH = 3; // e.g. DZS
const NUM_TOKENS = 5;

export const WordleLettersInput = ({ label, resource, source, validate, variant, margin, helperText, ...rest }) => {
  const translate = useTranslate();

  const {
    id,
    input,
    isRequired,
    meta: { error: extError, touched },
  } = useInput({ resource, source, validate, ...rest });

  const initialTokens = useMemo(() => {
    if (Array.isArray(input.value) && input.value.length) {
      return Array.from({ length: NUM_TOKENS }, (_, idx) => (input.value[idx] || '').toString().toUpperCase());
    }
    return Array.from({ length: NUM_TOKENS }, () => '');
  }, [input.value]);

  const [tokens, setTokens] = useState(initialTokens);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    setTokens(initialTokens);
  }, [initialTokens]);

  const validateTokens = useCallback((nextTokens) => {
    if (!nextTokens || nextTokens.length !== NUM_TOKENS) {
      return 'Must contain exactly 5 characters';
    }
    for (let i = 0; i < NUM_TOKENS; i += 1) {
      const t = (nextTokens[i] || '').toString().trim();
      if (!t) {
        return 'All 5 positions are required';
      }
      if (!/^[A-ZÁÉÍÓÖŐÚÜŰ]+$/.test(t)) {
        return 'Only letters are allowed';
      }
      if (t.length > MAX_TOKEN_LENGTH) {
        return `Max ${MAX_TOKEN_LENGTH} letters per position`;
      }
    }
    return '';
  }, []);

  const propagateChange = useCallback(
    (nextTokens) => {
      const err = validateTokens(nextTokens);
      setLocalError(err);
      input.onChange(nextTokens);
    },
    [input, validateTokens],
  );

  const handleTokenChange = useCallback(
    (index) => (event) => {
      const raw = (event.target.value || '').toString();
      const value = raw.toUpperCase().replace(/\s+/g, '');
      if (value.length > MAX_TOKEN_LENGTH) {
        return;
      }
      const next = [...tokens];
      next[index] = value;
      setTokens(next);
      propagateChange(next);
    },
    [tokens, propagateChange],
  );

  const LabelComponent = (
    <FieldTitle
      label={label || translate(`resources.${resource}.fields.${source}`)}
      source={source}
      resource={resource}
      isRequired={isRequired}
    />
  );

  const effectiveError = touched && (extError || localError);

  return (
    <div {...sanitizeRestProps(rest)} style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 8, width: '100%' }}>
        {tokens.map((val, idx) => (
          <TextField
            key={`${id}-${idx}`}
            value={val}
            onChange={handleTokenChange(idx)}
            inputProps={{ maxLength: MAX_TOKEN_LENGTH, style: { textTransform: 'uppercase', textAlign: 'center' } }}
            variant={variant || 'filled'}
            margin={margin || 'dense'}
            placeholder={`${idx + 1}`}
            error={!!effectiveError}
            label={idx === 0 ? LabelComponent : undefined}
            helperText={
              idx === 0 ? (
                <InputHelperText touched={touched} error={effectiveError || undefined} helperText={helperText} />
              ) : undefined
            }
            style={{ flex: 1 }}
          />
        ))}
      </div>
      <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
        Allowed per position: single letters or multi-letter combos (e.g. SZ, TY, DZS)
      </div>
    </div>
  );
};

WordleLettersInput.defaultProps = {
  helperText: '',
  label: 'Letters',
  resource: '',
  source: '',
  margin: 'dense',
  variant: 'filled',
};

WordleLettersInput.propTypes = {
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  label: PropTypes.string,
  resource: PropTypes.string,
  source: PropTypes.string,
  margin: PropTypes.string,
  variant: PropTypes.string,
};

export default WordleLettersInput;
